import moment from "moment";
import { withCsatToken } from "helpers/csatToken";
import {
  formatPhoneAnswerForPayload,
  getPhoneDialCode,
  getPhoneCountry,
} from "components/common/PhoneNumberInput";

/**
 * Process answers - handles files, formats phone numbers, removes empty values
 * @param {Object} answeres - Raw answers object from Redux
 * @param {Array} questions - Questions array to validate against
 * @returns {Object} - { processedAnswers, filesArray, isAllowGoogleQR }
 */
const processAnswers = (answeresCopy, questions) => {
  const filesArray = [];
  let isAllowGoogleQR = false;

  for (let item in answeresCopy) {
    const question = questions.find((q) => q._id === item);

    if (question !== undefined) {
      // Handle file type questions
      if ("isFile" in answeresCopy[item]) {
        answeresCopy[item].questionType =
          question?.questionType === "handwrittenInput"
            ? "base64"
            : question?.questionType;
        filesArray.push({ [item]: answeresCopy[item] });
      }

      // Format phone numbers: E.164 value + dial code + ISO country
      if (
        question.questionType === "text" &&
        question.textType === "phone" &&
        answeresCopy[item].value !== ""
      ) {
        const formatted = formatPhoneAnswerForPayload(answeresCopy[item]);
        if (formatted) {
          answeresCopy[item] = formatted;
        }
      }

      // Remove empty or null values
      if (
        !answeresCopy[item]?.value ||
        (Array.isArray(answeresCopy[item].value) &&
          answeresCopy[item].value.length === 0)
      ) {
        delete answeresCopy[item];
      }

      // Check for Google review option
      if (
        answeresCopy[item]?.value &&
        question?.options?.find(
          (opt) => opt?._id?.toString() === answeresCopy[item].value?.toString()
        )?.allow_google_review
      ) {
        isAllowGoogleQR = true;
      }
    }
  }

  return { processedAnswers: answeresCopy, filesArray, isAllowGoogleQR };
};

/**
 * Calculate total survey score
 * @param {Array} allQuestions - All questions in the survey
 * @returns {Number} - Total max score
 */
const calculateSurveyTotal = (allQuestions) => {
  let surveyTotal = 0;
  for (let index = 0; index < allQuestions?.length; index++) {
    const question = allQuestions[index];
    if (!question?.skip) {
      surveyTotal += question?.maxScore ? question?.maxScore : 0;
    }
  }
  return surveyTotal;
};

/**
 * Build metadata object with all survey information
 * @param {Object} options - Configuration options
 * @returns {Object} - Complete metadata object
 */
const buildMetaData = (options = {}) => {
  const {
    customValues = null,
    contactFormData = null,
    guest_info = null,
    visitDate = null,
    visitTimeIn = null,
    selectedTimeOut = null,
    survey_without_login = null,
    showContactForm = false,
  } = options;

  // Preparing metadata for the survey response
  const metaData = {
    survey_id: localStorage.getItem("survey") || "",
    location_id: localStorage.getItem("location") || "",
    response_user_id: localStorage.getItem("user_id") || "",
    tag_id: localStorage.getItem("tag_id")
      ? localStorage.getItem("tag_id")
      : localStorage.getItem("tag"),
    guest_info,
  };
  withCsatToken(metaData); // CSAT email link: the partial belongs to that email
  if (survey_without_login?.visit_information) {
    metaData.visitDate = visitDate;
    metaData.visitTimeIn = visitTimeIn;
    metaData.visitTimeOut = selectedTimeOut
      ? moment(selectedTimeOut).format("HH:mm")
      : null;
  }
  if (survey_without_login?.guest_info) {
    metaData.guest_info = guest_info;
  }

  // Handling audit and email data if available
  const audit = localStorage.getItem("audit") ?? null;
  const email = localStorage.getItem("email") ?? null;
  if (audit) {
    metaData.audit = audit;
    metaData.email = email;
  }

  // Add Login user detail
  const userEmail = localStorage.getItem("user_email") ?? null;
  if (userEmail) {
    metaData.email = userEmail;
  }

  const override_id = localStorage.getItem("override_id") ?? null;
  if (override_id) {
    metaData.override_id = override_id;
  }

  const task_id = localStorage.getItem("task_id") ?? null;
  if (task_id) {
    metaData.task_id = task_id;
  }

  const is_override = localStorage.getItem("is_override") ?? null;
  if (is_override) {
    metaData.is_override = is_override;
  }

  const override_date = localStorage.getItem("override_date") ?? null;
  if (override_date) {
    metaData.override_date = override_date;
  }

  const response_user_id = localStorage.getItem("response_user_id") ?? null;
  if (response_user_id) {
    metaData.response_user_id = response_user_id;
  }
  // Add custom values to the metadata
  if (customValues && typeof customValues === 'object') {
    const formattedData = Object.entries(customValues).map(([key, value]) => ({
      fieldKey: key,
      value: value,
    }));
    if (formattedData?.length > 0) {
      metaData.customValues = formattedData;
    }
  }

  if (showContactForm && contactFormData && Object.keys(contactFormData).length > 0) {
    const contactInfo = {
      name: contactFormData.name || "",
      email: contactFormData.email || "",
      phone: contactFormData.phone || "",
      country:
        contactFormData.phoneCountry ||
        getPhoneCountry(contactFormData.phone) ||
        undefined,
      code:
        getPhoneDialCode(
          contactFormData.phone,
          contactFormData.phoneCountry
        ) || undefined,
      customValues: [],
    };

    if (survey_without_login?.contact_custom_fields?.length > 0) {
      survey_without_login.contact_custom_fields.forEach((field) => {
        if (contactFormData[field.fieldKey]) {
          contactInfo.customValues.push({
            fieldKey: field.fieldKey,
            fieldName: field.fieldName,
            fieldType: field.fieldType,
            value: contactFormData[field.fieldKey],
          });
        }
      });
    }

    metaData.contactInfo = contactInfo;

    if (survey_without_login?.helpdesk_create_ticket) {
      metaData.helpdesk_create_ticket = true;
    }
  }

  return metaData;
};

/**
 * Append files to FormData object
 * @param {FormData} formData - FormData object to append to
 * @param {Array} filesArray - Array of file objects to process
 */
const appendFilesToFormData = (formData, filesArray) => {
  if (filesArray.length) {
    filesArray.forEach((item) => {
      const key = Object.keys(item)[0];
      const { questionType } = item[key];
      let images = [];

      // Processing image files based on question type
      if (questionType === "file" || questionType === "handwrittenInput") {
        if (item[key]?.value?.length) {
          item[key].value.forEach((img) => images.push(img));
        }
        if (item[key]?.image?.length) {
          item[key].image.forEach((img) => images.push(img));
        }
      } else {
        images = item[key]?.image || [];
      }

      // Appending images to formData
      if (images.length) {
        images.forEach((image) => {
          formData.append(key, image);
        });
      }
    });
  }
};

/**
 * Remove empty or null values from the answersCopy object
 * @param {Object} answeresCopy - Answers object to remove empty or null values from
 * @returns {Object} - Answers object with empty or null values removed
 */
const removeEmptyOrNullValuesFromAnswersCopy = (answeresCopy) => {
  for (let item in answeresCopy) {
    if (
      !answeresCopy[item]?.value ||
      (Array.isArray(answeresCopy[item].value) &&
        answeresCopy[item].value.length === 0)
    ) {
      delete answeresCopy[item];
    }
  }
  return answeresCopy;
};

/** Take YYYY-MM-DD from a full timestamp (UTC date only, ignores time). */
const toDateOnly = (value) => {
  const date = moment.utc(value);
  return date.isValid() ? date.format("YYYY-MM-DD") : null;
};

/**
 * Show question when schedule_windows is missing/empty, or today's UTC date
 * falls within any window's start/end dates (inclusive, date-only).
 * Backend stores windows as UTC calendar days (e.g. Aug 1 00:00Z–Aug 10 23:59Z).
 * @param {Object} question
 * @param {Date} [now=new Date()]
 * @returns {boolean}
 */
const isQuestionVisibleBySchedule = (question, now = new Date()) => {
  const windows = question?.schedule_windows;
  if (!Array.isArray(windows) || windows.length === 0) {
    return true;
  }

  const today = moment.utc(now).format("YYYY-MM-DD");

  return windows.some((window) => {
    const start = toDateOnly(window?.start);
    const end = toDateOnly(window?.end);
    if (!start || !end) return false;
    return today >= start && today <= end;
  });
};

/**
 * Filter survey questions by schedule_windows (if present and non-empty).
 * @param {Array} questions
 * @param {Date} [now=new Date()]
 * @returns {Array}
 */
const filterQuestionsByScheduleWindows = (questions, now = new Date()) => {
  if (!Array.isArray(questions)) {
    return [];
  }
  return questions.filter((question) =>
    isQuestionVisibleBySchedule(question, now)
  );
};

/**
 * Visible list filters: schedule_windows + employees/inventory location gating.
 * Use this when rebuilding from allQuestionsOriginal (e.g. tablet journey).
 * Keep allQuestionsOriginal unfiltered so hide/route indices stay stable.
 *
 * @param {Array} questions
 * @param {Object} [options]
 * @param {boolean} [options.hasLocation]
 * @param {Date} [options.now]
 * @returns {Array}
 */
const filterVisibleSurveyQuestions = (
  questions,
  {
    hasLocation = Boolean(
      typeof localStorage !== "undefined" && localStorage.getItem("location")
    ),
    now = new Date(),
  } = {}
) => {
  let filtered = filterQuestionsByScheduleWindows(questions, now);
  if (!hasLocation) {
    filtered = filtered.filter(
      (item) =>
        item.questionType !== "employees" &&
        item.questionType !== "inventory"
    );
  }
  return filtered;
};

export {
  processAnswers,
  calculateSurveyTotal,
  buildMetaData,
  appendFilesToFormData,
  removeEmptyOrNullValuesFromAnswersCopy,
  isQuestionVisibleBySchedule,
  filterQuestionsByScheduleWindows,
  filterVisibleSurveyQuestions,
};
